'use client';

/**
 * PickerDropdown — single-select trigger + menu primitive.
 *
 * Per Figma 14434:187614 (Storybook/Primitives/DropdownMenu).
 *
 *   ┌────────────────────┐
 *   │  May            ▾  │   trigger:  border, rounded-input, px-6 py-4
 *   └────────────────────┘   text:     Inter Semi Bold 15px, off-black
 *   ┌──────────────┬─────┐
 *   │  Jan         │     │   menu:     bg-off-white, rounded-input,
 *   │  Feb         │  │  │             border-thin dinnerplate-white,
 *   │  Mar         │  │  │             p-2, gap-2
 *   │  ████ May ██ │  ▓  │   items:    p-4, rounded-lg, 15px Inter
 *   │  Jun         │  │  │             normal: text-grey
 *   │  Jul         │  │  │             selected: bg-dinnerplate-white-light
 *   │              │     │                       text-off-black
 *   └──────────────┴─────┘             hover:   bg-dinnerplate-white-lighter
 *      scrollable      lane: w-6, p-2, w-2 pill thumb (DOM-based,
 *                            mirrors the Primitives/Scrollbar
 *                            DefaultVertical story)
 *
 * Used by CalendarDatePicker for the month/year row. Designed to be the
 * canonical primitive for any small single-select that needs the picker
 * styling rather than the action-style shadcn `dropdown-menu`.
 *
 * Behaviours:
 *   - The popover is responsive — height grows with the viewport via
 *     Radix's `--radix-popover-content-available-height`, floored to a
 *     whole-item height so the last visible item never half-cuts.
 *   - The trigger chevron rotates 180° while the menu is open.
 *   - The scrollbar lane is a sibling DOM element with a synced draggable
 *     thumb — the native webkit scrollbar is hidden because Chrome's pseudo-
 *     elements can't replicate the primitive's 6px pill geometry reliably
 *     across platforms.
 *   - Inside a Radix Dialog, react-remove-scroll blocks wheel events on
 *     the portalled content. A non-passive wheel listener restores
 *     scrolling without affecting non-modal usage.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronDownIcon } from '@icons/ChevronDownIcon';
import { cn } from '@/lib/utils';

export interface PickerDropdownItem {
  label: string;
  value: string;
}

export interface PickerDropdownProps {
  /** Currently selected item value (controlled). */
  value: string;
  items: PickerDropdownItem[];
  onSelect: (value: string) => void;
  /** ClassName forwarded to the trigger button. */
  className?: string;
  /**
   * CSS max-height for the scroll container. Defaults to a responsive
   * formula that floors `--radix-popover-content-available-height` to a
   * whole-item multiple so the last visible item never half-cuts.
   */
  maxHeight?: string;
  /**
   * Distance (px) the menu keeps clear of the viewport bottom. Use this on
   * pages with a fixed footer so the menu doesn't open behind it.
   */
  collisionBottom?: number;
}

// Each item row is 46.5px tall (15px text + p-[12px] = 46.5) and items are
// separated by a 6px gap; the scroll container has 6px top/bottom padding.
// To make the last visible item land flush (never half-cut), we floor the
// available height to a multiple of (46.5 + 6) = 52.5px and add back the 6px
// top padding. No fixed cap — grows with the viewport.
const ITEM_ROW_PITCH = 52.5;
const SCROLL_PADDING_TOP = 6;
const ALL_ITEMS_FALLBACK = ITEM_ROW_PITCH * 12 + SCROLL_PADDING_TOP; // 636px
const RESPONSIVE_MAX_HEIGHT = `calc(round(down, var(--radix-popover-content-available-height, ${ALL_ITEMS_FALLBACK}px) - ${SCROLL_PADDING_TOP}px, ${ITEM_ROW_PITCH}px) + ${SCROLL_PADDING_TOP}px)`;

const TRIGGER_CLASS =
  'flex w-full items-center justify-between rounded-input border border-dinnerplate-white bg-transparent px-6 py-4 font-inter text-[15px] font-semibold leading-normal text-off-black shadow-field outline-none transition-colors focus-visible:outline-none focus-visible:ring-0';
const ITEM_BASE_CLASS =
  'flex w-full items-center justify-start p-4 rounded-lg font-inter text-[15px] font-normal leading-normal text-grey outline-none transition-colors hover:bg-dinnerplate-white-lighter focus-visible:outline-none focus-visible:ring-0';
const ITEM_SELECTED_CLASS =
  'bg-dinnerplate-white-light text-off-black hover:bg-dinnerplate-white-light';

// Hide the native webkit scrollbar — the side lane below replaces it.
const NATIVE_SCROLLBAR_HIDDEN_CLASS =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:size-0';

/**
 * Synced side-lane scrollbar — 18px lane with `p-[6px]` around a 6px
 * dinnerplate-white pill thumb (per Primitives/Scrollbar DefaultVertical
 * story 14439:188060). Renders as a sibling of the scroll container so
 * the native overlay scrollbar can stay hidden.
 */
function SideScrollbar({
  scrollContainerEl,
}: {
  scrollContainerEl: HTMLDivElement | null;
}) {
  const [thumb, setThumb] = useState({ top: 0, height: 0, range: 0, show: false });
  const laneRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startY: number;
    startScrollTop: number;
    ratio: number;
  } | null>(null);

  const recalc = useCallback(() => {
    if (!scrollContainerEl || !laneRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerEl;
    const laneHeight = laneRef.current.clientHeight;
    const innerLane = laneHeight - 12; // p-2 (6px) top + bottom
    // +1 absorbs sub-pixel rounding so a non-overflowing list doesn't flicker
    // the scrollbar in/out on fractional clientHeight/scrollHeight values.
    if (scrollHeight > clientHeight + 1) {
      const h = Math.max(20, (clientHeight / scrollHeight) * innerLane);
      const range = innerLane - h;
      const scrollable = scrollHeight - clientHeight;
      const top = scrollable > 0 ? (scrollTop / scrollable) * range : 0;
      setThumb({ top, height: h, range, show: true });
    } else {
      setThumb({ top: 0, height: 0, range: 0, show: false });
    }
  }, [scrollContainerEl]);

  useEffect(() => {
    recalc();
    if (!scrollContainerEl) return;
    scrollContainerEl.addEventListener('scroll', recalc);
    const ro = new ResizeObserver(recalc);
    ro.observe(scrollContainerEl);
    return () => {
      scrollContainerEl.removeEventListener('scroll', recalc);
      ro.disconnect();
    };
  }, [recalc, scrollContainerEl]);

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      if (!scrollContainerEl || !dragRef.current) return;
      const { startY, startScrollTop, ratio } = dragRef.current;
      const scrollable = scrollContainerEl.scrollHeight - scrollContainerEl.clientHeight;
      scrollContainerEl.scrollTop = Math.max(
        0,
        Math.min(scrollable, startScrollTop + (event.clientY - startY) * ratio),
      );
    };
    const handleUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [scrollContainerEl]);

  const onThumbDown = (event: React.PointerEvent) => {
    event.preventDefault();
    if (!scrollContainerEl || thumb.range <= 0) return;
    const scrollable = scrollContainerEl.scrollHeight - scrollContainerEl.clientHeight;
    dragRef.current = {
      startY: event.clientY,
      startScrollTop: scrollContainerEl.scrollTop,
      ratio: scrollable / thumb.range,
    };
  };

  // When the list fits without overflow, hide the entire lane (no border,
  // no 18px reserve) so the items expand to the full popover width. The
  // hidden lane keeps a ref'd mount point so future resizes can re-show
  // it without remounting the synced-thumb effect chain.
  return (
    <div
      ref={laneRef}
      aria-hidden={!thumb.show}
      className={cn(
        'relative shrink-0 bg-off-white',
        thumb.show
          ? 'w-6 p-2 border-l-thin border-dinnerplate-white'
          : 'w-0 p-0 border-0',
      )}
    >
      {thumb.show && (
        <div
          onPointerDown={onThumbDown}
          style={{ position: 'absolute', top: thumb.top + 6, left: 6, height: thumb.height }}
          className="w-2 rounded-full bg-dinnerplate-white hover:bg-grey-light transition-colors cursor-grab active:cursor-grabbing select-none"
        />
      )}
    </div>
  );
}

export function PickerDropdown({
  value,
  items,
  onSelect,
  className,
  maxHeight = RESPONSIVE_MAX_HEIGHT,
  collisionBottom = 0,
}: PickerDropdownProps) {
  const [open, setOpen] = useState(false);
  // Callback ref + state pairing: Radix mounts the Portal content
  // asynchronously, so a plain useRef is still null when a [open]-dep'd
  // effect first fires. Storing the element in state forces the effect
  // to re-run when the node actually exists.
  const [scrollContainerEl, setScrollContainerEl] =
    useState<HTMLDivElement | null>(null);

  // Pin max-height on first open. The `maxHeight` prop is a CSS pre-paint
  // estimate via `--radix-popover-content-available-height` (and is the only
  // value available before mount, or where the CSS `round()` it uses is
  // unsupported); once the popover mounts we snapshot the same snapped value
  // into JS state and apply THAT instead (see render below). That keeps the
  // menu from growing mid-session if the viewport or page-scroll changes what
  // Radix reports as available. Reset to null on close so the next open
  // re-measures against the current viewport.
  const [pinnedMaxHeight, setPinnedMaxHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setPinnedMaxHeight(null);
      return;
    }
    if (!scrollContainerEl) return;
    if (pinnedMaxHeight !== null) return;
    const raw = getComputedStyle(scrollContainerEl)
      .getPropertyValue('--radix-popover-content-available-height')
      .trim();
    const available = parseFloat(raw);
    if (!Number.isFinite(available)) return;
    const usable = available - SCROLL_PADDING_TOP;
    const visibleCount = Math.floor(usable / ITEM_ROW_PITCH);
    const snapped = visibleCount * ITEM_ROW_PITCH + SCROLL_PADDING_TOP;
    setPinnedMaxHeight(snapped);
  }, [open, scrollContainerEl, pinnedMaxHeight]);

  const handleSelect = useCallback(
    (itemValue: string) => {
      onSelect(itemValue);
      setOpen(false);
    },
    [onSelect],
  );

  // Non-passive wheel listener restores scroll inside Radix Dialogs (where
  // react-remove-scroll blocks portalled wheel events).
  useEffect(() => {
    if (!scrollContainerEl) return;

    const onWheel = (event: WheelEvent) => {
      const canScrollVertically =
        scrollContainerEl.scrollHeight > scrollContainerEl.clientHeight;
      if (!canScrollVertically) return;
      event.preventDefault();
      event.stopPropagation();
      scrollContainerEl.scrollTop += event.deltaY;
    };

    scrollContainerEl.addEventListener('wheel', onWheel, { passive: false });
    return () => scrollContainerEl.removeEventListener('wheel', onWheel);
  }, [scrollContainerEl]);

  const selectedLabel =
    items.find((item) => item.value === value)?.label ?? value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(TRIGGER_CLASS, className)}>
          <span>{selectedLabel}</span>
          <ChevronDownIcon
            className={cn(
              'shrink-0 size-5 text-off-black transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] rounded-input border-thin border-dinnerplate-white bg-off-white p-0 shadow-soft animate-dropdown-in motion-reduce:animate-none"
        align="start"
        collisionPadding={{ bottom: collisionBottom }}
      >
        <div className="relative flex items-stretch overflow-hidden rounded-input">
          <div
            ref={setScrollContainerEl}
            className={cn(
              'flex-1 overflow-y-auto bg-off-white p-2',
              NATIVE_SCROLLBAR_HIDDEN_CLASS,
            )}
            style={{
              maxHeight:
                pinnedMaxHeight !== null ? `${pinnedMaxHeight}px` : maxHeight,
            }}
          >
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleSelect(item.value)}
                  className={cn(
                    ITEM_BASE_CLASS,
                    item.value === value && ITEM_SELECTED_CLASS,
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <SideScrollbar scrollContainerEl={scrollContainerEl} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
